<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import NavTreeItem from './NavTreeItem.vue'
import SettingsNavTree from './SettingsNavTree.vue'

import { useNavigation, useNavStore, type BuiltNavNode } from '@/app/navigation'
import { AuthBrandLogo } from '@/features/auth'
import { FreeTrialPlanCard } from '@/features/credits'
import { useI18n } from '@/shared/i18n'
import { Drawer } from '@/shared/ui/drawer'

const route = useRoute()
const { t } = useI18n()
const navStore = useNavStore()
const { items } = useNavigation(() => route)
const mainItems = computed(() => items.value.filter((item) => item.placement !== 'bottom'))
const bottomItems = computed(() => items.value.filter((item) => item.placement === 'bottom'))
const desktopRail = ref<HTMLElement | null>(null)
const hoverExpanded = ref(false)
// `isSidebarExpanded` already folds `showSettingsNav` in, so the rail force-expands for the whole
// takeover — v3 parity (`SideNavItems.vue:371`, `isPinnedSide || isInSettings`).
const desktopExpanded = computed(() => navStore.isSidebarExpanded || hoverExpanded.value)
// The takeover (v3 `SideNavItems.vue:7/177` — one `<Transition>`, `v-if="!isInSettings"` /
// `v-else`): inside `/settings` the settings tree REPLACES the main tree rather than joining it,
// and the way back rides with it. Driven off the route by `navStore.syncOnNavigation`.
const settingsOpen = computed(() => navStore.isSettingsOpen)

const mobileOpen = computed({
  get: () => navStore.isPinnedMobileSide,
  set: (value: boolean) => {
    if (value !== navStore.isPinnedMobileSide) navStore.toggleMobileSidebar()
  },
})

function onNavigate(_item: BuiltNavNode): void {
  navStore.closeMobileSidebar()
}

function onDesktopFocusOut(event: FocusEvent): void {
  const next = event.relatedTarget
  if (!(next instanceof Node) || !desktopRail.value?.contains(next)) hoverExpanded.value = false
}
</script>

<template>
  <aside
    id="app-navigation-desktop"
    ref="desktopRail"
    class="app-sidebar sticky top-[4.5rem] z-sticky h-[calc(100dvh-4.5rem)] w-20 overflow-hidden bg-surface px-3.5 py-md transition-[width,box-shadow] duration-300 motion-reduce:transition-none max-[56rem]:hidden"
    :class="{ 'app-sidebar--expanded w-72 shadow-pop': desktopExpanded }"
    :data-expanded="desktopExpanded"
    @mouseenter="hoverExpanded = true"
    @mouseleave="hoverExpanded = false"
    @focusin="hoverExpanded = true"
    @focusout="onDesktopFocusOut"
  >
    <nav
      class="app-sidebar__navigation flex h-full min-h-0 flex-col"
      :aria-label="settingsOpen ? t('shell.nav.settings') : t('shell.nav.label')"
    >
      <SettingsNavTree v-if="settingsOpen" :expanded="desktopExpanded" @navigate="onNavigate" />
      <template v-else>
        <ul
          v-if="mainItems.length"
          class="app-sidebar__tree app-sidebar__tree--main grid py-1 w-full grid-cols-[minmax(0,1fr)] content-start gap-2xs m-0 min-h-0 flex-auto list-none overflow-y-auto p-0"
        >
          <NavTreeItem
            v-for="item in mainItems"
            :key="item.navKey ?? item.labelKey ?? item.to?.name ?? 'nav-item'"
            :item="item"
            :expanded="desktopExpanded"
            @navigate="onNavigate"
          />
        </ul>
        <p
          v-else-if="bottomItems.length === 0"
          class="app-sidebar__empty m-0 whitespace-nowrap p-xs text-[0.8125rem] leading-normal text-muted"
        >
          {{ t('shell.nav.empty') }}
        </p>
        <FreeTrialPlanCard class="mx-auto mb-xs flex-none" :expanded="desktopExpanded" />
        <ul
          v-if="bottomItems.length"
          class="app-sidebar__tree app-sidebar__tree--bottom grid w-full flex-none grid-cols-[minmax(0,1fr)] gap-2xs m-0 mt-auto list-none bg-surface p-0 pt-xs"
          data-testid="sidebar-bottom-navigation"
        >
          <NavTreeItem
            v-for="item in bottomItems"
            :key="item.navKey ?? item.labelKey ?? item.to?.name ?? 'bottom-nav-item'"
            :item="item"
            :expanded="desktopExpanded"
            @navigate="onNavigate"
          />
        </ul>
      </template>
    </nav>
  </aside>

  <Drawer
    v-model="mobileOpen"
    class="app-sidebar__drawer"
    placement="left"
    :width="settingsOpen ? '20rem' : '18rem'"
    :title="t('shell.nav.label')"
    :close-label="t('shell.nav.close')"
  >
    <template v-if="settingsOpen" #header>
      <span class="app-sidebar__drawer-brand flex w-full items-center justify-center">
        <AuthBrandLogo />
        <span class="app-sidebar__drawer-brand-label sr-only">{{ t('shell.nav.settings') }}</span>
      </span>
    </template>
    <nav
      id="app-navigation-mobile"
      class="flex min-h-full flex-col"
      :aria-label="settingsOpen ? t('shell.nav.settings') : t('shell.nav.label')"
    >
      <SettingsNavTree v-if="settingsOpen" expanded @navigate="onNavigate" />
      <template v-else>
        <ul
          v-if="mainItems.length"
          class="app-sidebar__tree app-sidebar__tree--main grid w-full flex-auto grid-cols-[minmax(0,1fr)] gap-2xs m-0 list-none p-0"
        >
          <NavTreeItem
            v-for="item in mainItems"
            :key="item.navKey ?? item.labelKey ?? item.to?.name ?? 'nav-item'"
            :item="item"
            expanded
            @navigate="onNavigate"
          />
        </ul>
        <p
          v-else-if="bottomItems.length === 0"
          class="app-sidebar__empty m-0 whitespace-nowrap p-xs text-[0.8125rem] leading-normal text-muted"
        >
          {{ t('shell.nav.empty') }}
        </p>
        <FreeTrialPlanCard class="mt-md" expanded />
        <ul
          v-if="bottomItems.length"
          class="app-sidebar__tree app-sidebar__tree--bottom grid w-full flex-none grid-cols-[minmax(0,1fr)] gap-2xs m-0 mt-md list-none border-t border-border p-0 pt-md"
          data-testid="mobile-sidebar-bottom-navigation"
        >
          <NavTreeItem
            v-for="item in bottomItems"
            :key="item.navKey ?? item.labelKey ?? item.to?.name ?? 'bottom-nav-item'"
            :item="item"
            expanded
            @navigate="onNavigate"
          />
        </ul>
      </template>
    </nav>
  </Drawer>
</template>

<style scoped>
.app-sidebar__drawer-brand :deep(.auth-brand-logo) {
  max-height: 2.5rem;
  width: 8.625rem;
  object-fit: contain;
}
</style>
